import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { batchAPI } from '../services/api';

const ImageUpload = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(''); // New state for status message

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const newFiles = fileArray.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: (file.size / 1024 / 1024).toFixed(2),
      file: file,
    }));
    setUploadedFiles([...uploadedFiles, ...newFiles]);
  };

  const removeFile = (id) => {
    setUploadedFiles(uploadedFiles.filter(file => file.id !== id));
  };

  const handleUpload = async () => {
    if (uploadedFiles.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading files...');

    try {
      // Step 1: Upload files
      const formData = new FormData();
      uploadedFiles.forEach(fileObj => {
        formData.append('files', fileObj.file);
      });

      console.log('📤 Uploading files...');
      const response = await batchAPI.upload(batchId, formData, (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      });

      console.log(`✅ Uploaded ${response.data.length} images successfully`);

      // Step 2: Automatically trigger validation
      setUploadStatus('Upload complete! Starting validation...');
      console.log('🔄 Starting automatic validation...');
      setUploadProgress(0); // Reset for validation progress

      await batchAPI.validate(batchId);
      console.log('✅ Validation triggered');

      // Step 3: Poll for validation results
      setUploadStatus('Validating images...');
      console.log('⏳ Waiting for validation to complete...');
      await pollValidationResults();

      // Step 4: Navigate to results
      setUploadStatus('Validation complete! Redirecting...');
      alert('✅ Upload and validation complete! View your results.');
      navigate(`/batch/${batchId}`);

    } catch (error) {
      console.error('Upload/Validation error:', error);

      let errorMessage = 'Upload failed. Please try again.';

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setUploadStatus('');
      alert('❌ ' + errorMessage);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const pollValidationResults = async () => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 60; // 60 seconds max wait

      const interval = setInterval(async () => {
        attempts++;

        try {
          const progressRes = await batchAPI.getValidationProgress(batchId);
          const { processed_images, total_images, current_status } = progressRes.data;

          // Update progress
          const validationProgress = Math.round((processed_images / total_images) * 100);
          setUploadProgress(validationProgress);

          console.log(`Validation progress: ${processed_images}/${total_images} (${validationProgress}%)`);

          // Check if validation is complete
          if (current_status === 'waiting' || current_status === 'errors' || current_status === 'approved') {
            clearInterval(interval);
            console.log(`✅ Validation complete with status: ${current_status}`);
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(interval);
            console.log('⚠️ Validation timeout - taking too long');
            resolve(); // Resolve anyway and let user see results
          }
        } catch (error) {
          console.error('Error checking validation progress:', error);
          clearInterval(interval);
          reject(error);
        }
      }, 1000); // Check every second
    });
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
    },
    header: {
      backgroundColor: 'white',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      borderBottom: '1px solid #e5e7eb',
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '20px 30px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    titleSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
    },
    logo: {
      width: '48px',
      height: '48px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '24px',
      fontWeight: 'bold',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0,
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      margin: '2px 0 0 0',
    },
    backButton: {
      padding: '10px 20px',
      backgroundColor: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    mainContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '40px 30px',
    },
    uploadCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      padding: '40px',
      marginBottom: '30px',
    },
    dropZone: {
      border: '3px dashed #cbd5e1',
      borderRadius: '12px',
      padding: '60px 40px',
      textAlign: 'center',
      transition: 'all 0.3s',
      cursor: 'pointer',
    },
    dropZoneActive: {
      borderColor: '#667eea',
      backgroundColor: '#f0f4ff',
    },
    uploadIcon: {
      fontSize: '64px',
      marginBottom: '20px',
    },
    uploadTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '8px',
    },
    uploadSubtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '20px',
    },
    browseButton: {
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    fileInput: {
      display: 'none',
    },
    supportedFormats: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '16px',
    },
    filesCard: {
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      border: '1px solid #e5e7eb',
      padding: '24px',
    },
    filesHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
    },
    filesTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0,
    },
    clearButton: {
      padding: '8px 16px',
      backgroundColor: '#fee2e2',
      color: '#ef4444',
      border: 'none',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    fileList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    fileItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb',
    },
    fileInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    fileIcon: {
      fontSize: '24px',
    },
    fileName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1f2937',
    },
    fileSize: {
      fontSize: '12px',
      color: '#6b7280',
    },
    removeButton: {
      padding: '6px 12px',
      backgroundColor: 'white',
      color: '#ef4444',
      border: '1px solid #ef4444',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    uploadButtonContainer: {
      marginTop: '24px',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
    },
    cancelUploadButton: {
      padding: '14px 32px',
      backgroundColor: '#f3f4f6',
      color: '#374151',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    confirmUploadButton: {
      padding: '14px 32px',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#9ca3af',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      marginTop: '16px',
      overflow: 'hidden',
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      transition: 'width 0.3s',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <div style={styles.logo}>C</div>
            <div>
              <h1 style={styles.title}>Upload Images</h1>
              <p style={styles.subtitle}>Batch ID: {batchId}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/batch/${batchId}`)}
            style={styles.backButton}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
          >
            ← Back to Batch
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Upload Area */}
        <div style={styles.uploadCard}>
          <div
            style={{
              ...styles.dropZone,
              ...(dragActive ? styles.dropZoneActive : {}),
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
          >
            <div style={styles.uploadIcon}>📁</div>
            <h3 style={styles.uploadTitle}>
              {dragActive ? 'Drop files here' : 'Drag & drop files here'}
            </h3>
            <p style={styles.uploadSubtitle}>or click to browse</p>
            <button
              style={styles.browseButton}
              onMouseEnter={(e) => e.target.style.opacity = '0.9'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
              onClick={(e) => {
                e.stopPropagation();
                document.getElementById('fileInput').click();
              }}
            >
              Browse Files
            </button>
            <input
              id="fileInput"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.tif,.tiff,.pdf"
              onChange={handleChange}
              style={styles.fileInput}
            />
            <p style={styles.supportedFormats}>
              Supported formats: JPG, PNG, TIF, TIFF, PDF (Max 10MB per file)
            </p>
          </div>
        </div>

        {/* Files List */}
        <div style={styles.filesCard}>
          <div style={styles.filesHeader}>
            <h3 style={styles.filesTitle}>
              Selected Files ({uploadedFiles.length})
            </h3>
            {uploadedFiles.length > 0 && (
              <button
                onClick={() => setUploadedFiles([])}
                style={styles.clearButton}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#fecaca'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#fee2e2'}
              >
                Clear All
              </button>
            )}
          </div>

          {uploadedFiles.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
              <p>No files selected yet</p>
            </div>
          ) : (
            <>
              <div style={styles.fileList}>
                {uploadedFiles.map((file) => (
                  <div key={file.id} style={styles.fileItem}>
                    <div style={styles.fileInfo}>
                      <div style={styles.fileIcon}>📄</div>
                      <div>
                        <div style={styles.fileName}>{file.name}</div>
                        <div style={styles.fileSize}>{file.size} MB</div>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(file.id)}
                      style={styles.removeButton}
                      disabled={uploading}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#fee2e2';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'white';
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              {uploading && (
                <>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${uploadProgress}%` }} />
                  </div>
                  <div style={{
                    textAlign: 'center',
                    marginTop: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#667eea'
                  }}>
                    {uploadStatus} {uploadProgress}%
                  </div>
                </>
              )}

              <div style={styles.uploadButtonContainer}>
                <button
                  onClick={() => navigate(`/batch/${batchId}`)}
                  style={styles.cancelUploadButton}
                  disabled={uploading}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  style={{
                    ...styles.confirmUploadButton,
                    opacity: uploading ? 0.5 : 1,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                  }}
                  onMouseEnter={(e) => !uploading && (e.target.style.opacity = '0.9')}
                  onMouseLeave={(e) => !uploading && (e.target.style.opacity = '1')}
                >
                  {uploading
                    ? uploadStatus || `Uploading... ${uploadProgress}%`
                    : `Upload & Validate ${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'File' : 'Files'}`
                  }
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;